import { AnyAction } from 'redux';
import {
    RESET_STATE,
    GET_ALL_USERS,
    GET_ALL_USERS_LOADING,
    GET_USER,
    ADD_USER,
    IS_PRINCIPAL_NAME_AVAILABLE,
    RESET_USER_PASSWORD,
    USERS_COUNT,
    EXPIRE_USER_PASSWORD,
    REMOVE_ROLE_MEMBERSHIP,
    IMPORT_USERS,
    REMOVE_GROUP_MEMBERSHIP,
    REVOKE_PUSH,
    REVOKE_U2F,
    REVOKE_OTP,
    IMPORT_USERS_ERROR,
    UPDATE_USER_ATTRIBUTE,
    UPDATE_USER_ATTRIBUTES,
    UPDATE_USER_ATTRIBUTE_RESET,
    ADD_USER_ATTRIBUTE,
    DELETE_USER_ATTRIBUTE,
    SET_USER_APNS,
    SET_USER_PATCH_DATA,
    ADD_ROLES_MEMBERSHIP,
    ADD_GROUPS_MEMBERSHIP,
    REMOVE_EXPIRE_USER_PASSWORD_RESPONSE
} from '@app/store/constants';
import { IUserAttribute, IUserState, IUserDetail } from '@app/models';
import { u2fEnabledAttributeName, otpEnabledAttributeName } from '@app/helpers';

const initialState: IUserState = {
    users: [],
    count: 0,
    usersLoading: false,
    user: {} as IUserDetail,
    addUser: {},
    principalName: true,
    resetUserPasswordResponse: {},
    expireUserPasswordResponse: {},
    removeRoleMembershipResponse: {},
    removeGroupMembershipResponse: {},
    revokePushResponse: {},
    revokeU2fResponse: {},
    revokeOtpResponse: {},
    importUsers: {},
    importUsersError: false,
    updateUserAttribute: {},
    deleteUserAttribute: {},
    addRolesMembership: {},
    addGroupsMembership: {}
};

export function users(state = initialState, action: AnyAction) {
    const { payload, type, importantData } = action;

    switch (type) {
        case RESET_STATE:
            return initialState;
        case GET_ALL_USERS:
            return {
                ...state,
                users: payload
            };
        case GET_ALL_USERS_LOADING:
            return {
                ...state,
                usersLoading: payload
            };

        case GET_USER:
            return {
                ...state,
                user: payload
            };

        case ADD_USER:
            return {
                ...state,
                addUser: payload
            };

        case IS_PRINCIPAL_NAME_AVAILABLE:
            return {
                ...state,
                principalName: payload
            };

        case RESET_USER_PASSWORD:
            return {
                ...state,
                resetUserPasswordResponse: payload
            };
        case EXPIRE_USER_PASSWORD:
            let updateDate: IUserDetail = { ...state.user };
            updateDate.IsPasswordExpired = true;
            updateDate.PasswordLastChangedNoReset = new Date();
            return {
                ...state,
                expireUserPasswordResponse: payload,
                user: updateDate
            };
        case REMOVE_EXPIRE_USER_PASSWORD_RESPONSE:
            return {
                ...state,
                expireUserPasswordResponse: {},
            };
        case REMOVE_ROLE_MEMBERSHIP:
            return {
                ...state,
                removeRoleMembershipResponse: payload
            };
        case REMOVE_GROUP_MEMBERSHIP:
            return {
                ...state,
                removeGroupMembershipResponse: payload
            };
        case USERS_COUNT:
            return {
                ...state,
                count: payload
            };

        case IMPORT_USERS:
            return {
                ...state,
                importUsers: payload
            };
        case IMPORT_USERS_ERROR:
            return {
                ...state,
                importUsersError: payload
            };
        case REVOKE_PUSH:
            // if the payload represents success then remove it from state
            if (payload && payload.status >= 200 && payload.status < 300) {
                let change: any = { ...state.user };

                change.IdentityAuthenticators = [];

                return {
                    ...state,
                    user: change
                };
            }

            return {
                ...state,
                revokePushResponse: payload
            };
        case REVOKE_U2F:
            // if the payload represents success then remove it from state
            if (payload && payload.status >= 200 && payload.status < 300) {
                let change: any = { ...state.user };

                change.Attributes = change.Attributes.filter(function (item: IUserAttribute) {
                    return item.Key !== u2fEnabledAttributeName;
                });

                return {
                    ...state,
                    user: change
                };
            }

            return {
                ...state,
                revokeU2fResponse: payload
            };
        case REVOKE_OTP:
            // if the payload represents success then remove it from state
            if (payload && payload.status >= 200 && payload.status < 300) {
                let change: any = { ...state.user };

                change.Attributes = change.Attributes.filter(function (item: IUserAttribute) {
                    return item.Key !== otpEnabledAttributeName;
                });

                return {
                    ...state,
                    user: change
                };
            }

            return {
                ...state,
                revokeOtpResponse: payload
            };
        case UPDATE_USER_ATTRIBUTE:
            // if the payload represents success then update state
            if (payload && payload.status >= 200 && payload.status < 300 && importantData) {
                let change: any = { ...state.user };

                let returnedData: any = JSON.parse(importantData);

                change.Attributes.forEach((item: IUserAttribute) => {
                    if (item.Id === returnedData.Id) {
                        item.Key = returnedData.Key;
                        item.Value = returnedData.Value;
                    }
                });
            }

            return {
                ...state,
                updateUserAttribute: payload
            };
        case UPDATE_USER_ATTRIBUTE_RESET:
            return {
                ...state,
                updateUserAttribute: initialState.updateUserAttribute
            };
        case UPDATE_USER_ATTRIBUTES:
            return {
                ...state,
                user: {
                    ...state.user,
                    Attributes: payload
                }
            };
        case ADD_USER_ATTRIBUTE:
            return {
                ...state,
                updateUserAttribute: payload
            };
        case DELETE_USER_ATTRIBUTE:
            // if the payload represents success then update state
            if (payload && payload.status >= 200 && payload.status < 300 && importantData) {
                let change: any = { ...state.user };

                // remove attribute from state
                change.Attributes = change.Attributes.filter(function (item: IUserAttribute) {
                    return item.Id !== importantData;
                });

                return {
                    ...state,
                    user: change
                };
            }

            return {
                ...state,
                deleteUserAttribute: payload
            };

        case SET_USER_APNS:
            let change: any = { ...state.user };

            change.AlternatePrincipalNames = change.AlternatePrincipalNames.filter(function (e: any) {
                return payload.map((x: { Id: any; }) => x.Id).indexOf(e.Id) > -1;
            });

            return {
                ...state,
                user: change
            };

        case SET_USER_PATCH_DATA:
            let userPatch: any = { ...state.user };

            for (let patchItem in payload) {
                userPatch[patchItem] = payload[patchItem];
            }

            return {
                ...state,
                user: userPatch
            };

        case ADD_ROLES_MEMBERSHIP:
            return {
                ...state,
                addRolesMembership: payload
            };

        case ADD_GROUPS_MEMBERSHIP:
            return {
                ...state,
                addGroupsMembership: payload
            };

        default:
            return state;
    }
}
